/**
 * src/domain/arcade/lockstep-engine.ts
 *
 * Simultaneous Commit-Reveal Lockstep State Machine (Step 3.2).
 * Features:
 * - Synchronized Turn Tick Engine (10-second planning ticks).
 * - Blind Commit-Reveal action vector cryptography (anti-cheat).
 * - Strict initiative resolution: HACK_EMP(100) > DEPLOY_SHIELD(80) > MOVE(50) > FIRE(30).
 * - Automatic STALLED_DEFEND timeout mitigation with AP penalties and 2-strike forfeit.
 * - Deterministic Merkle State Root validation for desync prevention.
 */

import {
  TacticalUnit,
  UnitAction,
  PlayerActionVector,
  MerkleGameState,
  computeStateRoot,
  sha256Sync,
} from './game-state-contracts';

export type LockstepPhase = 'COMMIT' | 'REVEAL' | 'RESOLVED' | 'FORFEIT';

export interface LockstepMatch {
  matchId: string;
  player1Did: string;
  player2Did: string;
  tickNumber: number;
  phase: LockstepPhase;
  planningDeadline: number;
  revealDeadline: number;
  commitments: Record<string, string>; // playerDid -> commitmentHash
  reveals: Record<string, { actionVector: PlayerActionVector; salt: string }>;
  units: Record<string, TacticalUnit>;
  gridDimensions: { width: number; height: number };
  consecutiveStalls: Record<string, number>; // playerDid -> count
  apPenaltyNextTurn: Record<string, number>; // playerDid -> penalty
  stateRoot: string;
  isGameOver: boolean;
  winnerDid?: string;
  historyStateRoots: string[];
}

export class LockstepEngine {
  public static readonly PLANNING_WINDOW_MS = 10_000; // 10s
  public static readonly REVEAL_WINDOW_MS = 5_000;    // 5s
  public static readonly MAX_AP_PER_TURN = 5;
  public static readonly STALL_AP_PENALTY = 2;
  public static readonly CONSECUTIVE_STALL_LIMIT = 2;

  private static instance: LockstepEngine;
  private matches: Map<string, LockstepMatch> = new Map();

  private constructor() {}

  public static getInstance(): LockstepEngine {
    if (!LockstepEngine.instance) {
      LockstepEngine.instance = new LockstepEngine();
    }
    return LockstepEngine.instance;
  }

  /**
   * Computes action commitment hash: SHA256(ActionVectorJSON + Salt + TickNumber + MatchId)
   */
  public static computeCommitment(
    actionVector: PlayerActionVector,
    salt: string,
    matchId: string
  ): string {
    const vectorJson = JSON.stringify(actionVector);
    return sha256Sync(`${vectorJson}:${salt}:${actionVector.tickNumber}:${matchId}`);
  }

  /**
   * Initializes a new lockstep match with 3 units per player.
   */
  public createMatch(params: {
    matchId: string;
    player1Did: string;
    player2Did: string;
    customUnits?: Record<string, TacticalUnit>;
  }): LockstepMatch {
    const { matchId, player1Did, player2Did } = params;

    const units: Record<string, TacticalUnit> =
      params.customUnits || this.generateDefaultSquads(player1Did, player2Did);

    const gridDimensions = { width: 10, height: 10 };
    const initialStateRoot = computeStateRoot(1, units, gridDimensions);
    const now = Date.now();

    const match: LockstepMatch = {
      matchId,
      player1Did,
      player2Did,
      tickNumber: 1,
      phase: 'COMMIT',
      planningDeadline: now + LockstepEngine.PLANNING_WINDOW_MS,
      revealDeadline: now + LockstepEngine.PLANNING_WINDOW_MS + LockstepEngine.REVEAL_WINDOW_MS,
      commitments: {},
      reveals: {},
      units,
      gridDimensions,
      consecutiveStalls: { [player1Did]: 0, [player2Did]: 0 },
      apPenaltyNextTurn: { [player1Did]: 0, [player2Did]: 0 },
      stateRoot: initialStateRoot,
      isGameOver: false,
      historyStateRoots: [initialStateRoot],
    };

    this.matches.set(matchId, match);
    return match;
  }

  /**
   * Submits a hidden action commitment during COMMIT phase.
   */
  public submitCommitment(matchId: string, playerDid: string, commitmentHash: string): void {
    const match = this.matches.get(matchId);
    if (!match) throw new Error(`Match ${matchId} not found.`);
    if (match.phase !== 'COMMIT') throw new Error(`Cannot commit in ${match.phase} phase.`);
    if (playerDid !== match.player1Did && playerDid !== match.player2Did) {
      throw new Error(`DID ${playerDid} is not a player in this match.`);
    }

    match.commitments[playerDid] = commitmentHash;

    // Transition to REVEAL once both players committed
    if (match.commitments[match.player1Did] && match.commitments[match.player2Did]) {
      match.phase = 'REVEAL';
      match.revealDeadline = Date.now() + LockstepEngine.REVEAL_WINDOW_MS;
    }
  }

  /**
   * Reveals action vector and secret salt during REVEAL phase.
   */
  public submitReveal(
    matchId: string,
    playerDid: string,
    actionVector: PlayerActionVector,
    salt: string
  ): void {
    const match = this.matches.get(matchId);
    if (!match) throw new Error(`Match ${matchId} not found.`);
    if (match.phase !== 'REVEAL') {
      // Auto-advance if past planning deadline
      if (match.phase === 'COMMIT' && Date.now() >= match.planningDeadline) {
        match.phase = 'REVEAL';
      } else {
        throw new Error(`Cannot reveal in ${match.phase} phase.`);
      }
    }

    const commitment = match.commitments[playerDid];
    if (!commitment) {
      throw new Error(`No commitment recorded for player ${playerDid}.`);
    }

    // Preimage validation
    const expected = LockstepEngine.computeCommitment(actionVector, salt, matchId);
    if (expected !== commitment) {
      throw new Error('Preimage verification failed: action vector or salt does not match commitment.');
    }

    match.reveals[playerDid] = { actionVector, salt };
  }

  /**
   * Resolves the simultaneous tick:
   * 1. Detects unrevealed players -> assigns STALLED_DEFEND & AP penalties.
   * 2. Executes actions in strict initiative order:
   *    HACK_EMP(100) > DEPLOY_SHIELD(80) > MOVE(50) > FIRE(30)
   * 3. Computes post-tick Merkle state root.
   * 4. Cleans up destroyed units and tests victory conditions.
   */
  public resolveTick(matchId: string): {
    state: MerkleGameState;
    executedActions: UnitAction[];
    events: string[];
    isGameOver: boolean;
    winnerDid?: string;
  } {
    const match = this.matches.get(matchId);
    if (!match) throw new Error(`Match ${matchId} not found.`);
    if (match.isGameOver) {
      throw new Error(`Match ${matchId} is already over.`);
    }

    const events: string[] = [];
    const allActions: UnitAction[] = [];

    // 1. Check for player reveals or assign STALLED_DEFEND fallback
    const players = [match.player1Did, match.player2Did];
    for (const pDid of players) {
      if (!match.reveals[pDid]) {
        match.consecutiveStalls[pDid] = (match.consecutiveStalls[pDid] || 0) + 1;
        events.push(`Player ${pDid.slice(0, 12)} stalled turn #${match.tickNumber}! Defaulting to DEFEND.`);

        // 2-strike forfeit rule
        if (match.consecutiveStalls[pDid] >= LockstepEngine.CONSECUTIVE_STALL_LIMIT) {
          match.isGameOver = true;
          match.phase = 'FORFEIT';
          const opponent = pDid === match.player1Did ? match.player2Did : match.player1Did;
          match.winnerDid = opponent;
          events.push(`Player ${pDid.slice(0, 12)} forfeited due to ${LockstepEngine.CONSECUTIVE_STALL_LIMIT} consecutive unrevealed turns!`);

          return {
            state: {
              matchId,
              tickNumber: match.tickNumber,
              units: match.units,
              gridDimensions: match.gridDimensions,
              stateRoot: match.stateRoot,
              timestamp: Date.now(),
            },
            executedActions: [],
            events,
            isGameOver: true,
            winnerDid: opponent,
          };
        }

        // Apply AP penalty next turn
        match.apPenaltyNextTurn[pDid] = LockstepEngine.STALL_AP_PENALTY;

        // Assign defensive posture to living units
        for (const u of Object.values(match.units)) {
          if (u.ownerDid === pDid && u.hp > 0) {
            allActions.push({
              unitId: u.id,
              type: 'STALLED_DEFEND',
              costAp: 0,
              initiative: 10,
            });
          }
        }
      } else {
        match.consecutiveStalls[pDid] = 0;
        allActions.push(...match.reveals[pDid].actionVector.actions);
      }
    }

    // 2. Strict Initiative Sorting (Descending: 100 -> 80 -> 50 -> 30 -> 10)
    allActions.sort((a, b) => b.initiative - a.initiative);

    // 3. Deterministic Action Execution
    for (const action of allActions) {
      const unit = match.units[action.unitId];
      if (!unit || unit.hp <= 0) continue;

      // Skip action if unit is currently EMP disabled
      if (unit.isEmpDisabled && action.type !== 'STALLED_DEFEND') {
        events.push(`Unit ${unit.id} is EMP disabled and cannot act!`);
        continue;
      }

      switch (action.type) {
        case 'HACK_EMP': {
          if (action.targetCoord) {
            const targetUnit = this.findUnitAt(match.units, action.targetCoord);
            if (targetUnit && targetUnit.ownerDid !== unit.ownerDid) {
              targetUnit.isEmpDisabled = true;
              events.push(`Unit ${unit.id} hacked ${targetUnit.id} with EMP!`);
            }
          }
          break;
        }

        case 'DEPLOY_SHIELD': {
          unit.isShielded = true;
          events.push(`Unit ${unit.id} deployed directional shield!`);
          break;
        }

        case 'MOVE': {
          if (action.targetCoord) {
            const { x, y } = action.targetCoord;
            if (
              x >= 0 &&
              x < match.gridDimensions.width &&
              y >= 0 &&
              y < match.gridDimensions.height &&
              !this.findUnitAt(match.units, action.targetCoord)
            ) {
              unit.coord = { x, y };
              events.push(`Unit ${unit.id} moved to (${x}, ${y})`);
            }
          }
          break;
        }

        case 'FIRE': {
          if (action.targetCoord) {
            const target = this.findUnitAt(match.units, action.targetCoord);
            if (target && target.ownerDid !== unit.ownerDid) {
              if (target.isShielded) {
                target.isShielded = false;
                events.push(`Unit ${unit.id} fired at ${target.id}! Shield absorbed the hit and broke!`);
              } else {
                target.hp = Math.max(0, target.hp - 35);
                events.push(`Unit ${unit.id} hit ${target.id} for 35 damage! (${target.hp}/${target.maxHp} HP remaining)`);
                if (target.hp === 0) {
                  events.push(`Unit ${target.id} was destroyed!`);
                }
              }
            }
          }
          break;
        }

        case 'STALLED_DEFEND': {
          unit.isShielded = true;
          events.push(`Unit ${unit.id} took defensive stance.`);
          break;
        }
      }
    }

    // 4. Remove destroyed units
    for (const [id, u] of Object.entries(match.units)) {
      if (u.hp <= 0) {
        delete match.units[id];
      }
    }

    // 5. Test Victory Conditions
    const p1Units = Object.values(match.units).filter((u) => u.ownerDid === match.player1Did);
    const p2Units = Object.values(match.units).filter((u) => u.ownerDid === match.player2Did);

    if (p1Units.length === 0 && p2Units.length === 0) {
      match.isGameOver = true;
      events.push('Mutual destruction! Match ended in a draw.');
    } else if (p1Units.length === 0) {
      match.isGameOver = true;
      match.winnerDid = match.player2Did;
      events.push(`Player ${match.player2Did.slice(0, 12)} won the match!`);
    } else if (p2Units.length === 0) {
      match.isGameOver = true;
      match.winnerDid = match.player1Did;
      events.push(`Player ${match.player1Did.slice(0, 12)} won the match!`);
    }

    // 6. Reset AP and advance tick
    const nextTick = match.tickNumber + 1;
    for (const u of Object.values(match.units)) {
      const penalty = match.apPenaltyNextTurn[u.ownerDid] || 0;
      u.ap = Math.max(1, u.maxAp - penalty);
      u.isEmpDisabled = false; // EMP clears at end of tick
    }
    match.apPenaltyNextTurn[match.player1Did] = 0;
    match.apPenaltyNextTurn[match.player2Did] = 0;

    const newStateRoot = computeStateRoot(nextTick, match.units, match.gridDimensions);
    match.stateRoot = newStateRoot;
    match.historyStateRoots.push(newStateRoot);

    if (!match.isGameOver) {
      match.tickNumber = nextTick;
      match.phase = 'COMMIT';
      match.commitments = {};
      match.reveals = {};
      const now = Date.now();
      match.planningDeadline = now + LockstepEngine.PLANNING_WINDOW_MS;
      match.revealDeadline = now + LockstepEngine.PLANNING_WINDOW_MS + LockstepEngine.REVEAL_WINDOW_MS;
    } else {
      match.phase = 'RESOLVED';
    }

    return {
      state: {
        matchId,
        tickNumber: match.tickNumber,
        units: match.units,
        gridDimensions: match.gridDimensions,
        stateRoot: match.stateRoot,
        timestamp: Date.now(),
      },
      executedActions: allActions,
      events,
      isGameOver: match.isGameOver,
      winnerDid: match.winnerDid,
    };
  }

  /**
   * Validates client state root against authoritative match state root (desync prevention).
   */
  public verifyClientStateRoot(matchId: string, clientStateRoot: string): boolean {
    const match = this.matches.get(matchId);
    if (!match) throw new Error(`Match ${matchId} not found.`);
    if (match.stateRoot !== clientStateRoot) {
      throw new Error(
        `DESYNC_DETECTED: Client root ${clientStateRoot.slice(0, 12)} diverges from authoritative root ${match.stateRoot.slice(0, 12)}.`
      );
    }
    return true;
  }

  public getMatch(matchId: string): LockstepMatch | undefined {
    return this.matches.get(matchId);
  }

  public clear(): void {
    this.matches.clear();
  }

  private findUnitAt(units: Record<string, TacticalUnit>, coord: { x: number; y: number }): TacticalUnit | undefined {
    return Object.values(units).find((u) => u.coord.x === coord.x && u.coord.y === coord.y);
  }

  private generateDefaultSquads(p1: string, p2: string): Record<string, TacticalUnit> {
    return {
      [`${p1}_scout`]: {
        id: `${p1}_scout`,
        ownerDid: p1,
        type: 'SCOUT',
        coord: { x: 1, y: 1 },
        hp: 75,
        maxHp: 75,
        ap: 5,
        maxAp: 5,
      },
      [`${p1}_heavy`]: {
        id: `${p1}_heavy`,
        ownerDid: p1,
        type: 'HEAVY',
        coord: { x: 1, y: 2 },
        hp: 120,
        maxHp: 120,
        ap: 4,
        maxAp: 4,
      },
      [`${p1}_tech`]: {
        id: `${p1}_tech`,
        ownerDid: p1,
        type: 'TECH',
        coord: { x: 1, y: 3 },
        hp: 90,
        maxHp: 90,
        ap: 5,
        maxAp: 5,
      },
      [`${p2}_scout`]: {
        id: `${p2}_scout`,
        ownerDid: p2,
        type: 'SCOUT',
        coord: { x: 8, y: 8 },
        hp: 75,
        maxHp: 75,
        ap: 5,
        maxAp: 5,
      },
      [`${p2}_heavy`]: {
        id: `${p2}_heavy`,
        ownerDid: p2,
        type: 'HEAVY',
        coord: { x: 8, y: 7 },
        hp: 120,
        maxHp: 120,
        ap: 4,
        maxAp: 4,
      },
      [`${p2}_tech`]: {
        id: `${p2}_tech`,
        ownerDid: p2,
        type: 'TECH',
        coord: { x: 8, y: 6 },
        hp: 90,
        maxHp: 90,
        ap: 5,
        maxAp: 5,
      },
    };
  }
}

export const lockstepEngine = LockstepEngine.getInstance();
